import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '../../contexts/I18nContext';
import { useOverdueReport } from '../../hooks/useTaskReports';
import type { OverdueBucket, ReportFilters } from '../../lib/taskReportsApi';
import { ReportCard, SectionState } from './common';
import { CHART_TOOLTIP_STYLE } from '../../lib/chartTheme';

// Overdue tasks (due date in the past, not completed/cancelled), bucketed by
// how late they are, with per-person / per-project counts and the task list.

const BUCKET_ORDER: OverdueBucket[] = ['d1_7', 'd7_30', 'd30plus'];
const BUCKET_COLORS: Record<OverdueBucket, string> = {
  d1_7: '#F59E0B',
  d7_30: '#FB923C',
  d30plus: '#F87171',
};

export function OverdueSection({ filters }: { filters: ReportFilters }) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useOverdueReport(filters);

  const chartData = data
    ? BUCKET_ORDER.map((bucket) => ({
        bucket,
        name: t(`reports.overdue.${bucket}`),
        count: data.buckets[bucket],
      }))
    : [];

  return (
    <ReportCard title={t('reports.overdue.title')}>
      <SectionState isLoading={isLoading} isError={isError} />
      {data && data.tasks.length === 0 && (
        <p className="text-sm text-gray-400">{t('reports.overdue.empty')}</p>
      )}
      {data && data.tasks.length > 0 && (
        <div className="space-y-5">
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {chartData.map((entry) => (
                    <Cell key={entry.bucket} fill={BUCKET_COLORS[entry.bucket]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">
                {t('reports.overdue.byPerson')}
              </p>
              <div className="flex flex-wrap gap-2">
                {data.byPerson.map((p) => (
                  <span
                    key={p.userId ?? 'unassigned'}
                    className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700"
                  >
                    {p.name ?? t('reports.overruns.unassigned')} ·{' '}
                    <span className="font-semibold">{p.count}</span>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">
                {t('reports.overdue.byProject')}
              </p>
              <div className="flex flex-wrap gap-2">
                {data.byProject.map((p) => (
                  <span
                    key={p.projectId}
                    className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700"
                  >
                    {p.code ?? p.name} · <span className="font-semibold">{p.count}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs">
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.hours.task')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.hours.project')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.hours.person')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.hours.status')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.overdue.dueDate')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.overdue.daysOverdue')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.tasks.map((task) => (
                  <tr key={task.id} className="border-b border-gray-100">
                    <td className="px-3 py-2 text-gray-900">
                      {task.identifier ? (
                        <span className="mr-1.5 text-xs text-gray-400">{task.identifier}</span>
                      ) : null}
                      {task.title}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{task.project?.name ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{task.assignee?.name ?? '—'}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: task.status.color }}
                        />
                        {task.status.name}
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">
                      {task.dueDate.slice(0, 10)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-red-600">
                      {task.daysOverdue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ReportCard>
  );
}
